"use client"

import { useState, useEffect } from "react"
import { createEvent, updateEvent } from "../comman/Event.js"

const Modal = ({ isOpen, onClose, onSubmit, eventToEdit }) => {
  const [eventData, setEventData] = useState({
    eventName: "",
    aboutEvent: "",
    activities: "",
    location: "",
    ticketPrice: "",
    maxSeats: "",
    date: "",
    timeFrom: "09:00",
    timeTo: "18:00",
    registration: false,
    status: false,
  })

  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState("")

  useEffect(() => {
    if (eventToEdit) {
      setEventData({
        eventName: eventToEdit.eventName || "",
        aboutEvent: eventToEdit.aboutEvent || "",
        activities: Array.isArray(eventToEdit.activities)
          ? eventToEdit.activities.join(", ")
          : eventToEdit.activities || "",
        location: eventToEdit.location || "",
        ticketPrice: eventToEdit.ticketPrice || "",
        maxSeats: eventToEdit.maxSeats || "",
        date: eventToEdit.date || "",
        timeFrom: eventToEdit.timeFrom || "09:00",
        timeTo: eventToEdit.timeTo || "18:00",
        registration: eventToEdit.registration || false,
        status: eventToEdit.status || false,
      })
    }
  }, [eventToEdit])

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target
    setEventData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (eventData.timeFrom >= eventData.timeTo) {
      setError("End time must be later than start time.")
      return
    }

    setIsSubmitting(true)
    setError("")

    try {
      const dataToSubmit = {
        ...eventData,
        activities: eventData.activities.split(",").map((item) => item.trim()),
      }

      let result
      if (eventToEdit) {
        result = await updateEvent(eventToEdit._id, dataToSubmit)
      } else {
        result = await createEvent(dataToSubmit)
      }
      onSubmit(result)
      onClose()
    } catch (err) {
      setError("Failed to save event. Please try again.")
    } finally {
      setIsSubmitting(false)
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
        <div className="p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-2xl font-semibold text-gray-800">{eventToEdit ? "Edit Event" : "Create Event"}</h2>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
              </svg>
            </button>
          </div>
          {error && <div className="text-red-500 mb-4">{error}</div>}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="eventName" className="block text-sm font-medium text-gray-700 mb-1">
                Event Name
              </label>
              <input
                type="text"
                id="eventName"
                name="eventName"
                value={eventData.eventName}
                onChange={handleChange}
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-[#12143F] focus:border-[#12143F]"
                required
              />
            </div>
            <div>
              <label htmlFor="aboutEvent" className="block text-sm font-medium text-gray-700 mb-1">
                About Event
              </label>
              <textarea
                id="aboutEvent"
                name="aboutEvent"
                value={eventData.aboutEvent}
                onChange={handleChange}
                rows={3}
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-[#12143F] focus:border-[#12143F]"
                required
              />
            </div>
            <div>
              <label htmlFor="activities" className="block text-sm font-medium text-gray-700 mb-1">
                Activities
              </label>
              <input
                type="text"
                id="activities"
                name="activities"
                value={eventData.activities}
                onChange={handleChange}
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-[#12143F] focus:border-[#12143F]"
                placeholder="Separate activities with commas"
                required
              />
            </div>
            <div>
              <label htmlFor="location" className="block text-sm font-medium text-gray-700 mb-1">
                Location
              </label>
              <input
                type="text"
                id="location"
                name="location"
                value={eventData.location}
                onChange={handleChange}
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-[#12143F] focus:border-[#12143F]"
                required
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="ticketPrice" className="block text-sm font-medium text-gray-700 mb-1">
                  Ticket Price
                </label>
                <input
                  type="number"
                  id="ticketPrice"
                  name="ticketPrice"
                  value={eventData.ticketPrice}
                  onChange={handleChange}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-[#12143F] focus:border-[#12143F]"
                  required
                />
              </div>
              <div>
                <label htmlFor="maxSeats" className="block text-sm font-medium text-gray-700 mb-1">
                  Max Seats
                </label>
                <input
                  type="number"
                  id="maxSeats"
                  name="maxSeats"
                  value={eventData.maxSeats}
                  onChange={handleChange}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-[#12143F] focus:border-[#12143F]"
                  required
                />
              </div>
            </div>
            <div>
              <label htmlFor="date" className="block text-sm font-medium text-gray-700 mb-1">
                Date
              </label>
              <input
                type="date"
                id="date"
                name="date"
                value={eventData.date}
                onChange={handleChange}
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-[#12143F] focus:border-[#12143F]"
                required
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="timeFrom" className="block text-sm font-medium text-gray-700 mb-1">
                  Start Time
                </label>
                <input
                  type="time"
                  id="timeFrom"
                  name="timeFrom"
                  value={eventData.timeFrom}
                  onChange={handleChange}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-[#12143F] focus:border-[#12143F]"
                  required
                />
              </div>
              <div>
                <label htmlFor="timeTo" className="block text-sm font-medium text-gray-700 mb-1">
                  End Time
                </label>
                <input
                  type="time"
                  id="timeTo"
                  name="timeTo"
                  value={eventData.timeTo}
                  onChange={handleChange}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-[#12143F] focus:border-[#12143F]"
                  required
                />
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="registration"
                name="registration"
                checked={eventData.registration}
                onChange={handleChange}
                className="h-4 w-4 text-[#12143F] focus:ring-[#12143F] border-gray-300 rounded"
              />
              <label htmlFor="registration" className="text-sm font-medium text-gray-700">
                Registration Required
              </label>
            </div>
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="status"
                name="status"
                checked={eventData.status}
                onChange={handleChange}
                className="h-4 w-4 text-[#12143F] focus:ring-[#12143F] border-gray-300 rounded"
              />
              <label htmlFor="status" className="text-sm font-medium text-gray-700">
                Active Event
              </label>
            </div>
          </form>
        </div>
        <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse rounded-b-lg">
          <button
            type="submit"
            onClick={handleSubmit}
            className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-[#12143F] text-base font-medium text-white hover:bg-opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#12143F] sm:ml-3 sm:w-auto sm:text-sm"
            disabled={isSubmitting}
          >
            {isSubmitting ? "Submitting..." : eventToEdit ? "Update Event" : "Create Event"}
          </button>
          <button
            type="button"
            onClick={onClose}
            className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#12143F] sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  )
}

export default Modal

